import type { ZodTypeAny } from 'zod';

import { applyOktaTokenInjection } from '../oktaTokenInject';
import type { RequestInput } from '../types';
import { HttpMethod, MIME_TYPE } from '../types';
import { refreshInitAfterAuth0Unauthorized } from './auth0TokenRefresh';
import { evaluateSession } from './handleSession';
import { urlWithLocale } from './locale';
import { logger } from './logger';
import { updateInitWithValidAuthHeader } from './updateInitWithValidAuthHeader';
import { getUrlString } from './url';

const validationCheck = (
    zodSchema: ZodTypeAny | undefined,
    url: string,
    status: number,
    responseJson: unknown,
    method: string | HttpMethod = HttpMethod.GET
) => {
    if (zodSchema) {
        const zodResult = zodSchema.safeParse(responseJson);

        if (!zodResult.success) {
            console.error(
                '[validationError]',
                `${method} - ${getUrlString(url)} [${status}]`,
                '\nerrors:',
                JSON.stringify(zodResult.error.issues, null, 2),
                '\nresponse:',
                JSON.stringify(responseJson, null, 2)
            );
        }
    }
};

async function parseResponse<T>({
    response,
    isJson,
    zodSchema,
    url,
    method = HttpMethod.GET,
}: {
    response: Response;
    isJson?: boolean;
    zodSchema?: ZodTypeAny;
    url: string;
    method?: string | HttpMethod;
}) {
    if (isJson) {
        const responseJson = (await response.json()) as T;
        validationCheck(zodSchema, url, response.status, responseJson, method);

        return responseJson;
    }

    return (await response.text()) as T;
}

// Each fetching attempt is triggered and the timeout to validate that the API call does not exceed its own time are here
const attemptFetch = async (
    url: RequestInput,
    timeoutDuration?: number,
    init?: RequestInit & {
        additionalSuccessCodes?: number[];
        withLocale?: boolean;
    }
): Promise<Response | Error> => {
    const controller = new AbortController();
    // when timeoutDuration is defined that means each attempt should have an expiration time
    const abortSignalId = timeoutDuration ? setTimeout(() => controller.abort(), timeoutDuration) : undefined;

    try {
        return await fetch(url, {
            ...init,
            signal: abortSignalId ? controller.signal : init?.signal,
        });
    } catch (error) {
        if (error instanceof Error) {
            if (error.name === 'AbortError') {
                logger.error(
                    '[ >> apiFetch << ] Fetch aborted due to timeout: %d ms for URL: %s, %o',
                    timeoutDuration,
                    getUrlString(url),
                    init
                );
                return new Error(`Fetch aborted due to timeout: ${timeoutDuration} ms for URL: ${getUrlString(url)}`);
            }
            return error;
        } else {
            logger.error('[ >> apiFetch << ] Unknown error occurred during fetch. %s, %o ', getUrlString(url), init);
            return new Error(`Unknown error occurred during fetch of ${getUrlString(url)}`);
        }
    } finally {
        clearTimeout(abortSignalId);
    }
};

// Helper function to prepare request parameters
const prepareRequest = (
    input: RequestInput,
    init?: RequestInit & {
        additionalSuccessCodes?: number[];
        withLocale?: boolean;
    }
) => {
    const url = init?.withLocale ? urlWithLocale(input) : input;
    const additionalSuccessCodes = init?.additionalSuccessCodes ?? [];
    return { url, additionalSuccessCodes };
};

// Handle retries logic -- Loop to re-try a new fetch
const handleRetries = async (
    url: RequestInfo,
    init?: RequestInit & {
        additionalSuccessCodes?: number[];
        withLocale?: boolean;
    },
    numberOfRetries?: number,
    timeoutDuration?: number
): Promise<Response | Error> => {
    let response: Response | Error | undefined;

    const attempts = numberOfRetries || 0;
    // if the number of numberOfRetries is defined it time duration should have a default value of 3 seconds
    const attemptDuration = attempts > 0 && timeoutDuration === undefined ? 3000 : timeoutDuration;

    for (let attempt = 0; attempt <= attempts; attempt++) {
        response = await attemptFetch(url, attemptDuration, init);
        if (response instanceof Response) break;

        logger.info(
            '[ >> apiFetch << ] Retrying fetch... Attempt %d for URL: %s, %o',
            attempt,
            getUrlString(url),
            init
        );
    }

    if (!response) {
        const error = new Error(`apiFetch failed after ${numberOfRetries} attempts for URL: ${getUrlString(url)}`);
        logger.error('[ >> apiFetch << ] %s, %o', error.message, init);
        return error;
    }

    return response;
};

// Parse and validate response
const parseAndValidateResponse = async <T>(
    response: Response,
    zodSchema: ZodTypeAny | undefined,
    url: string,
    init?: RequestInit
) => {
    const contentType = response.headers.get('Content-Type');
    const isJson = contentType?.includes(MIME_TYPE.json);
    return await parseResponse<T>({
        response,
        isJson,
        zodSchema,
        url,
        method: init?.method,
    });
};

// Extended Response type with parsed data
export interface ResponseWithParsedData<T> extends Response {
    parsedResponse: T;
}

type ApiFetchInit = RequestInit & {
    additionalSuccessCodes?: number[];
    withLocale?: boolean;
    preserveHttpStatusForAdditionalSuccessCodes?: boolean;
};

// Handle response status and return parsed data or error
const handleResponseCode = <T>(
    response: Response,
    parsedResponse: T,
    additionalSuccessCodes: number[],
    preserveHttpStatusForAdditionalSuccessCodes?: boolean
): T | ResponseWithParsedData<T> => {
    if (response.status === 204) {
        // HTTP 204 means a successful empty response
        return response as ResponseWithParsedData<T>;
    }
    const isSuccess =
        (response.status >= 200 && response.status < 300) || additionalSuccessCodes.includes(response.status);
    if (isSuccess) {
        if (
            preserveHttpStatusForAdditionalSuccessCodes &&
            !response.ok &&
            additionalSuccessCodes.includes(response.status)
        ) {
            (response as ResponseWithParsedData<T>).parsedResponse = parsedResponse;
            return response as ResponseWithParsedData<T>;
        }

        return parsedResponse;
    }

    // Log the error response without cloning to avoid "Body already consumed" error
    logger.error(
        '[ >> apiFetch << ] Response status: %d, URL: %s, Parsed data: %o',
        response.status,
        response.url,
        parsedResponse
    );

    // Attach the parsed response to the Response object for later use
    (response as ResponseWithParsedData<T>).parsedResponse = parsedResponse;

    return response as ResponseWithParsedData<T>;
};

export type ApiFetchResponse<T> = T | ResponseWithParsedData<T> | Error;

/**
 * Handles apiFetch response, checking for errors and returning data or null
 * @param result - The result from apiFetch
 * @returns The data if successful, null if error
 */
export const handleApiFetchResponse = <T>(result: ApiFetchResponse<T>): T | null => {
    if (result instanceof Error) {
        return null;
    }
    if (result instanceof Response) {
        return null;
    }
    // Check if it's a ResponseWithParsedData (has status property)
    if (
        result &&
        typeof result === 'object' &&
        'status' in result &&
        typeof (result as { status: unknown }).status === 'number'
    ) {
        return null;
    }
    return result;
};

// Handle response processing
const processFetchResponse = async <T>({
    fetchResponse,
    url,
    init,
    zodSchema,
    additionalSuccessCodes,
}: {
    fetchResponse: Response | null | Error;
    url: string;
    init?: ApiFetchInit;
    zodSchema?: ZodTypeAny;
    additionalSuccessCodes: number[];
}): Promise<ApiFetchResponse<T>> => {
    if (!fetchResponse) return new Error(`No response from ${url}`);

    if (fetchResponse instanceof Error) return fetchResponse;

    try {
        const parsedResponse = await parseAndValidateResponse<T>(fetchResponse, zodSchema, url, init);

        return handleResponseCode(
            fetchResponse,
            parsedResponse,
            additionalSuccessCodes,
            init?.preserveHttpStatusForAdditionalSuccessCodes
        );
    } catch (error) {
        if (error instanceof Error) {
            logger.error(
                '[ >> apiFetch << ] Error processing fetch response: %s, %o, %d',
                error.message,
                { stack: error.stack },
                additionalSuccessCodes
            );
            return error;
        } else {
            logger.error('[ >> apiFetch << ] Unknown error occurred during fetch. %d', additionalSuccessCodes);
            return new Error(`Unknown error occurred during fetch of ${url}`);
        }
    }
};

export const apiFetch = async <T>(
    input: RequestInput,
    init?: ApiFetchInit,
    zodSchema?: ZodTypeAny,
    numberOfRetries?: number,
    timeoutDuration?: number,
    performApiLogout?: (accessToken: string) => Promise<boolean>
): Promise<ApiFetchResponse<T>> => {
    // Inject Okta token (client-side or server-side)
    let final: RequestInit | undefined = await applyOktaTokenInjection(init);

    const { url, additionalSuccessCodes } = prepareRequest(input, final);
    const urlString = getUrlString(url);

    // Is Client Side?
    if (typeof window !== 'undefined') {
        await evaluateSession({ performApiLogout });

        final = updateInitWithValidAuthHeader({ url, init: final });
    }

    const fetchResponse = await handleRetries(urlString, final, numberOfRetries, timeoutDuration);

    const refreshedInit = await refreshInitAfterAuth0Unauthorized({
        fetchResponse,
        url,
        init: final,
        additionalSuccessCodes,
    });

    if (refreshedInit !== null) {
        const retryResponse = await handleRetries(urlString, refreshedInit, numberOfRetries, timeoutDuration);

        return processFetchResponse<T>({
            fetchResponse: retryResponse,
            url: urlString,
            init: refreshedInit,
            zodSchema,
            additionalSuccessCodes,
        });
    }

    return processFetchResponse<T>({
        fetchResponse,
        url: urlString,
        init: final,
        zodSchema,
        additionalSuccessCodes,
    });
};
